import { inject, Injectable } from '@angular/core';
import { catchError, from, map, Observable, of, Subscriber, switchMap } from 'rxjs';
import { Geolocation, PermissionStatus as GeoPermissionStatus, Position } from '@capacitor/geolocation';
import { OrlikfyLocationServices } from 'orlikfy-location-services';
import { PLATFORM_TOKEN, PlatformProvider } from '../tokens/platform.token';
import { CoordinatesModel } from '../interfaces';
import { PermissionStates } from '../enums';

@Injectable({ providedIn: 'root' })
export class GeolocationService {
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);

  isLocationServicesEnabled(): Observable<boolean> {
    if (!this.platformProvider.isNativeRun) return of(!!navigator.geolocation);

    return from(OrlikfyLocationServices.isEnabled()).pipe(
      map((result: { enabled: boolean }) => result.enabled),
      catchError(() => of(false))
    );
  }

  isPermissionGranted(): Observable<boolean> {
    if (!this.platformProvider.isNativeRun) return this.webCheckPermission();

    return from(Geolocation.checkPermissions()).pipe(
      map((result: GeoPermissionStatus) => result.location === PermissionStates.GRANTED),
      catchError(() => of(false))
    );
  }

  checkPermissionState(): Observable<PermissionStates> {
    if (!this.platformProvider.isNativeRun) return this.webCheckPermissionState();

    return from(Geolocation.checkPermissions()).pipe(
      map((result: GeoPermissionStatus) => result.location as PermissionStates),
      catchError(() => of(PermissionStates.DENIED))
    );
  }

  async requestPermissions(): Promise<boolean> {
    if (!this.platformProvider.isNativeRun) return !!navigator.geolocation;

    try {
      const status: GeoPermissionStatus = await Geolocation.checkPermissions();

      if (status.location === PermissionStates.GRANTED) return true;
      if (status.location === PermissionStates.DENIED) return false;

      const result: GeoPermissionStatus = await Geolocation.requestPermissions();
      return result.location === PermissionStates.GRANTED;
    } catch {
      return false;
    }
  }

  getCoordinates(): Observable<CoordinatesModel | null> {
    if (!this.platformProvider.isNativeRun) return this.webGetCurrentPosition();

    return this.isPermissionGranted().pipe(
      switchMap((permission: boolean) => {
        if (!permission) return of(null);

        // maximumAge: cache do 60s zamiast nowego fixa - bez timeoutów.
        return from(
          Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 })
        ).pipe(
          map((position: Position) => ({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          })),
          catchError(() => of(null))
        );
      })
    );
  }

  private webGetCurrentPosition(): Observable<CoordinatesModel | null> {
    if (!navigator.geolocation) return of(null);

    return new Observable<CoordinatesModel | null>((observer: Subscriber<CoordinatesModel | null>) => {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          observer.next({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          observer.complete();
        },
        () => {
          observer.next(null);
          observer.complete();
        }
      );
    });
  }

  private webCheckPermission(): Observable<boolean> {
    if (!navigator.permissions) return of(true);

    return from(navigator.permissions.query({ name: 'geolocation' })).pipe(
      map((status: PermissionStatus) => status.state !== 'denied'),
      catchError(() => of(true))
    );
  }

  private webCheckPermissionState(): Observable<PermissionStates> {
    if (!navigator.permissions) return of(PermissionStates.PROMPT);

    return from(navigator.permissions.query({ name: 'geolocation' })).pipe(
      map((status: PermissionStatus) => {
        if (status.state === 'granted') return PermissionStates.GRANTED;
        if (status.state === 'denied') return PermissionStates.DENIED;
        return PermissionStates.PROMPT;
      }),
      catchError(() => of(PermissionStates.PROMPT))
    );
  }
}
